import { useState } from "react";
import { MessageCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#EC5B24] transition-colors";

const FORMS = {
  trains: {
    title: "Train ticket enquiry", note: "Tell us your journey — our team checks seats and books your IRCTC ticket.",
    fields: [["from", "From station *"], ["to", "To station *"], ["date", "Journey date *", "date"], ["travellers", "Passengers *", "number"],
      ["class", "Class", "select", ["Sleeper (SL)", "AC 3 Tier (3A)", "AC 2 Tier (2A)", "AC First (1A)", "Chair Car (CC)", "Any"]]],
    msg: (v) => `Train enquiry\nFrom: ${v.from}\nTo: ${v.to}\nDate: ${v.date}\nPassengers: ${v.travellers}\nClass: ${v.class || "Any"}`,
  },
  buses: {
    title: "Bus ticket enquiry", note: "Share your route and we'll find the best bus and seats for you.",
    fields: [["from", "From city *"], ["to", "To city *"], ["date", "Journey date *", "date"], ["travellers", "Passengers *", "number"],
      ["class", "Bus type", "select", ["AC Sleeper", "Non-AC Sleeper", "AC Seater", "Non-AC Seater", "Any"]]],
    msg: (v) => `Bus enquiry\nFrom: ${v.from}\nTo: ${v.to}\nDate: ${v.date}\nPassengers: ${v.travellers}\nBus type: ${v.class || "Any"}`,
  },
  hotels: {
    title: "Hotel booking enquiry", note: "Tell us where and when — we'll send you hand-picked hotel options.",
    fields: [["from", "City / area *"], ["date", "Check-in *", "date"], ["to", "Check-out *", "date"], ["travellers", "Guests *", "number"],
      ["class", "Rooms", "select", ["1", "2", "3", "4", "5+"]]],
    msg: (v) => `Hotel enquiry\nCity: ${v.from}\nCheck-in: ${v.date}\nCheck-out: ${v.to}\nGuests: ${v.travellers}\nRooms: ${v.class || "1"}`,
  },
};

const Field = ({ f: [k, label, type, opts], v, set, tab }) => (
  <label className="text-xs font-semibold text-slate-500">{label}
    {type === "select" ? (
      <select data-testid={`enq-${tab}-${k}`} value={v[k] || ""} onChange={(e) => set({ ...v, [k]: e.target.value })} className={`${inputCls} mt-1 text-slate-900 font-normal bg-white`}>
        <option value="">Choose</option>{opts.map((o) => <option key={o}>{o}</option>)}
      </select>
    ) : (
      <input data-testid={`enq-${tab}-${k}`} type={type || "text"} min={type === "number" ? 1 : type === "date" ? (tab === "hotels" && k === "to" && v.date ? v.date : new Date().toISOString().slice(0, 10)) : undefined} value={v[k] || ""}
        onChange={(e) => set({ ...v, [k]: e.target.value })} className={`${inputCls} mt-1 text-slate-900 font-normal`} />
    )}
  </label>
);

export const TravelEnquiry = ({ tab }) => {
  const form = FORMS[tab.id];
  const phone = useAuth().config.support_phone || "7719264029";
  const [v, setV] = useState({ travellers: "1" });
  const ok = ["from", "to", "date", "travellers", "name"].every((k) => (v[k] || "").trim()) && /^\d{10}$/.test(v.phone || "") && (tab.id !== "hotels" || v.to > v.date);
  const text = `${form.msg(v)}\nName: ${v.name}\nPhone: ${v.phone}\n(sent from WanderNest website)`;
  return (
    <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_20px_60px_-20px_rgba(28,26,74,0.35)]" data-testid={`enquiry-${tab.id}`}>
      <div className="flex items-center gap-3">
        <span className="h-11 w-11 rounded-xl bg-orange-50 text-[#EC5B24] grid place-items-center"><tab.icon className="h-5 w-5" /></span>
        <div><h3 className="font-heading text-xl font-bold">{form.title}</h3><p className="text-sm text-slate-500">{form.note}</p></div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 mt-5">
        {form.fields.map((f) => <Field key={f[0]} f={f} v={v} set={setV} tab={tab.id} />)}
      </div>
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] mt-3 items-end">
        <Field f={["name", "Your name *"]} v={v} set={setV} tab={tab.id} />
        <Field f={["phone", "Mobile number *", "tel"]} v={v} set={(x) => setV({ ...x, phone: x.phone.replace(/\D/g, "").slice(0, 10) })} tab={tab.id} />
        <a data-testid={`enq-${tab.id}-whatsapp-btn`} href={ok ? `https://wa.me/91${phone}?text=${encodeURIComponent(text)}` : undefined} target="_blank" rel="noopener noreferrer"
          aria-disabled={!ok} className={`rounded-xl px-6 py-3 font-heading font-bold text-white flex items-center justify-center gap-2 transition-colors ${ok ? "bg-[#25D366] hover:bg-[#1EBE5A]" : "bg-slate-300 pointer-events-none"}`}>
          <MessageCircle className="h-5 w-5" /> Enquire on WhatsApp
        </a>
      </div>
      <p className="text-xs text-slate-500 mt-3">Opens WhatsApp with your details filled in, sent to our team on {phone}.</p>
    </div>
  );
};
